<script setup lang="ts">
import type { Component } from 'vue'

withDefaults(
  defineProps<{
    icon?: string | Component
    iconRight?: boolean
    fullWidth?: boolean
    outline?: boolean
    disabled?: boolean
  }>(),
  {
    icon: undefined,
  },
)

defineEmits<{
  (e: 'click'): void
}>()
</script>

<template>
  <button
    v-bind="$attrs"
    class="basic-button"
    :class="{ 'full-width': fullWidth, outline: outline, 'icon-right': iconRight }"
    :disabled="disabled"
    @click="$emit('click')"
  >
    <div class="icon-slot">
      <component :is="icon" v-if="icon" />
      <slot v-else name="icon"></slot>
    </div>
    <div class="text-slot">
      <slot></slot>
    </div>
  </button>
</template>

<style scoped>
.basic-button {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  height: 35px;
  padding-left: 12px;
  padding-right: 12px;
  gap: 8px;
  border-radius: 4px;
  background-color: var(--ullib-color-link);
  border: 1px solid var(--ullib-color-link);
  color: var(--ullib-color-contrast);
  .icon-slot * {
    stroke: var(--ullib-color-contrast) !important;
  }
  user-select: none;
  cursor: pointer;

  font-weight: 500;
  white-space: nowrap;
  justify-content: center;

  transition:
    background-color 0.1s ease-in,
    color 0.1s ease-in,
    border-color 0.1s ease-in;

  &.full-width {
    width: 100%;
  }

  &.outline {
    background-color: transparent;
    border-color: var(--ullib-color-primary);
    color: var(--ullib-color-primary);
    .icon-slot * {
      stroke: var(--ullib-color-primary) !important;
    }
  }

  &.icon-right {
    flex-direction: row-reverse;
  }
}

.basic-button .icon-slot:empty {
  display: none;
}

.basic-button:active,
.basic-button:hover {
  background-color: var(--ullib-color-primary);
  border-color: var(--ullib-color-primary);
  color: var(--ullib-color-contrast);

  .icon-slot * {
    stroke: var(--ullib-color-contrast) !important;
  }
}

.basic-button:disabled {
  background-color: var(--ullib-color-accent-3);
  border-color: var(--ullib-color-disabled-button);
  color: var(--ullib-color-disabled-button);
  cursor: not-allowed;

  .icon-slot * {
    stroke: var(--ullib-color-disabled-button) !important;
  }
}

.icon-slot * {
  transition: stroke 0.1s ease-in;
}
.basic-button:active .icon-slot * {
  stroke: var(--ullib-color-contrast) !important;
}

.icon-slot {
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-slot > * {
  display: block;
  max-width: 24px;
  max-height: 24px;
  width: auto;
  height: auto;
}

.text-slot {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
